/**
 * BrowserAuthClient — browser-side auth SDK for user-center.
 *
 * Handles: password login, SMS/email code login, registration,
 * OAuth popup/redirect, token refresh, logout.
 */

import { initCaptcha, verifyCaptcha } from "./captcha";
import type { CaptchaResult } from "./captcha";
import type {
  AuthorizeStartResult,
  BrowserLoginResult,
  LoginResult,
  OAuthAuthorizeResult,
  OAuthExchangeResult,
  RegisterResult,
  ThirdPartyCodeExchangeResult,
  TokenSession,
} from "./types";

// --- Options ---

export interface BrowserAuthClientOptions {
  issuer: string;
  clientId: string;
  redirectUri: string;
  scope: string;
  platformCode?: string;
  /** GeeTest v4 captchaId — if provided, SMS/email sending will require captcha verification. */
  captchaId?: string;
  fetchImpl?: typeof fetch;
  windowRef?: { open?: (url?: string, target?: string, features?: string) => unknown; location?: { href: string } };
  storageRef?: StorageLike;
}

export interface BrowserLoginOptions {
  state?: string;
  nonce?: string;
}

export interface PopupAuthPayload {
  access_token?: string;
  account?: string;
  [key: string]: unknown;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const SESSION_KEY = "auth-center-session";

// --- Client ---

export class BrowserAuthClient {
  private opts: BrowserAuthClientOptions;
  private fetch: typeof fetch;
  private win: NonNullable<BrowserAuthClientOptions["windowRef"]>;
  private storage: StorageLike;

  constructor(opts: BrowserAuthClientOptions) {
    this.opts = opts;
    this.fetch = opts.fetchImpl ?? fetch.bind(globalThis);
    this.win = (opts.windowRef ?? typeof window !== "undefined" ? window : {}) as NonNullable<
      BrowserAuthClientOptions["windowRef"]
    >;
    this.storage = opts.storageRef ?? (typeof localStorage !== "undefined" ? localStorage : memStorage());

    // Auto-initialize GeeTest if captchaId is provided
    if (opts.captchaId) {
      initCaptcha({ captchaId: opts.captchaId }).catch(() => {
        // Silently fail — will retry on first verifyCaptcha() call
      });
    }
  }

  // ─── Properties ───

  get platformCode(): string {
    return this.opts.platformCode ?? "";
  }

  set platformCode(code: string) {
    this.opts.platformCode = code;
  }

  // ─── Login ───

  /** Login with account (email/phone) + password. */
  async login(account: string, password: string, platformCode?: string): Promise<LoginResult> {
    const body: Record<string, unknown> = { account, password };
    const pc = platformCode ?? this.platformCode;
    if (pc) body.platform_code = pc;

    const data = await this.postJSON<LoginResult>("/api/v1/user/auth/login", body);
    this.saveSession(data);
    return data;
  }

  /** Login with email + verification code. */
  async emailCodeLogin(email: string, code: string, platformCode?: string): Promise<LoginResult> {
    const body: Record<string, unknown> = { email, code };
    const pc = platformCode ?? this.platformCode;
    if (pc) body.platform_code = pc;

    const data = await this.postJSON<LoginResult>("/api/v1/user/auth/email/code-login", body);
    this.saveSession(data);
    return data;
  }

  /** Login with phone + SMS code. */
  async phoneSMSLogin(phone: string, code: string, platformCode?: string): Promise<LoginResult> {
    const body: Record<string, unknown> = { phone, code };
    const pc = platformCode ?? this.platformCode;
    if (pc) body.platform_code = pc;

    const data = await this.postJSON<LoginResult>("/api/v1/user/auth/phone/sms-login", body);
    this.saveSession(data);
    return data;
  }

  // ─── Register ───

  /** Register with email + password. */
  async register(
    email: string,
    password: string,
    nickname?: string,
    platformCode?: string,
  ): Promise<RegisterResult> {
    const body: Record<string, unknown> = { email, password };
    if (nickname) body.nickname = nickname;
    const pc = platformCode ?? this.platformCode;
    if (pc) body.platform_code = pc;

    const data = await this.postJSON<RegisterResult>("/api/v1/user/auth/register", body);
    this.saveSession(data);
    return data;
  }

  /** Register with phone + SMS code + password. */
  async phoneRegister(
    phone: string,
    code: string,
    password: string,
    nickname?: string,
    platformCode?: string,
  ): Promise<RegisterResult> {
    const body: Record<string, unknown> = { phone, code, password };
    if (nickname) body.nickname = nickname;
    const pc = platformCode ?? this.platformCode;
    if (pc) body.platform_code = pc;

    const data = await this.postJSON<RegisterResult>("/api/v1/user/auth/phone/register", body);
    this.saveSession(data);
    return data;
  }

  // ─── Verification Codes ───

  /** Send email verification code. Requires GeeTest captcha if captchaId was configured. */
  async sendEmailCode(
    email: string,
    type: "login" | "register" = "login",
    captchaResult?: CaptchaResult,
  ): Promise<void> {
    const body: Record<string, unknown> = { email, type };
    const captcha = captchaResult ?? (this.opts.captchaId ? await verifyCaptcha() : undefined);
    if (captcha) {
      body.lot_number = captcha.lot_number;
      body.captcha_output = captcha.captcha_output;
      body.pass_token = captcha.pass_token;
      body.gen_time = captcha.gen_time;
    }
    await this.postJSON("/api/v1/user/email/send", body);
  }

  /** Send SMS verification code. Requires GeeTest captcha if captchaId was configured. */
  async sendSMSCode(
    phone: string,
    type: "login" | "register" = "login",
    captchaResult?: CaptchaResult,
  ): Promise<void> {
    const body: Record<string, unknown> = { phone, type };
    const captcha = captchaResult ?? (this.opts.captchaId ? await verifyCaptcha() : undefined);
    if (captcha) {
      body.captcha_id = this.opts.captchaId;
      body.lot_number = captcha.lot_number;
      body.captcha_output = captcha.captcha_output;
      body.pass_token = captcha.pass_token;
      body.gen_time = captcha.gen_time;
    }
    await this.postJSON("/api/v1/user/sms/send", body);
  }

  // ─── OAuth ───

  /** Get OAuth authorization URL. */
  async getOAuthUrl(provider: string, popup: boolean, redirectUri?: string, authRequestId?: string): Promise<OAuthAuthorizeResult> {
    const params = new URLSearchParams({ popup: popup ? "1" : "0" });
    if (redirectUri) params.set("redirect_uri", redirectUri);
    if (authRequestId) params.set("auth_request_id", authRequestId);
    const data = await this.getJSON<OAuthAuthorizeResult>(
      `/api/v1/user/oauth/${provider}/authorize?${params.toString()}`,
    );
    return data;
  }

  /** Login with OAuth via popup. Returns tokens via postMessage. */
  async loginWithOAuthPopup(provider: string): Promise<void> {
    const result = await this.getOAuthUrl(provider, true);
    const features = ["popup=yes", "width=540", "height=720", "resizable=yes", "scrollbars=yes"].join(",");
    this.win.open?.(result.authorize_url, "user-center-oauth", features);
  }

  /** Login with OAuth via redirect. After login, redirects back to current page or given URI. */
  async loginWithOAuthRedirect(provider: string, redirectUri?: string): Promise<void> {
    const target = redirectUri ?? this.win.location?.href ?? "";
    const result = await this.getOAuthUrl(provider, false, target);
    if (this.win.location) {
      this.win.location.href = result.authorize_url;
    }
  }

  /** Exchange OAuth code for tokens. */
  async exchangeOAuthCode(
    provider: string,
    code: string,
    state: string,
    platformCode?: string,
  ): Promise<OAuthExchangeResult> {
    const body: Record<string, unknown> = { code, state };
    const pc = platformCode ?? this.platformCode;
    if (pc) body.platform_code = pc;
    const data = await this.postJSON<OAuthExchangeResult>(
      `/api/v1/user/oauth/${provider}/exchange`,
      body,
    );
    if (data.access_token) {
      this.saveSession(data);
    }
    return data;
  }

  /**
   * Exchange a third-party authorization code for tokens.
   *
   * When an external app initiates OAuth login via `loginWithOAuthRedirect()`,
   * User Center redirects back with `?code=uc_xxx` in query params.
   * Call this method to exchange that code for access/refresh tokens.
   */
  async exchangeAuthCode(code: string): Promise<ThirdPartyCodeExchangeResult> {
    const data = await this.postJSON<ThirdPartyCodeExchangeResult>(
      "/api/v1/oauth2/exchange-code",
      { code },
    );
    if (data.access_token) {
      this.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token ?? "",
        expires_in: data.expires_in ?? 0,
        expires_at: data.expires_in ? Date.now() + data.expires_in * 1000 : undefined,
        account_id: data.account_id,
        display_id: data.display_id,
        account_type: data.account_type,
        email: data.email,
        nickname: data.nickname,
      });
    }
    return data;
  }

  // ─── SSO Redirect ───

  /**
   * Redirect the user to User Center for specific actions (e.g. email verification,
   * profile setup, bind phone). After the user clicks "Return to App" in User Center,
   * they will be redirected back to `returnUrl` with a `?code=uc_xxx` parameter.
   *
   * Call `handleUserCenterReturn()` on your callback page to exchange the code.
   */
  async redirectToUserCenter(options: {
    /** Path within User Center (e.g. "/profile", "/identities"). Default: "/" */
    path?: string;
    /** URL to redirect back to after the user clicks "Return to App". */
    returnUrl: string;
  }): Promise<void> {
    const session = this.getSession();
    if (!session?.access_token) {
      throw new Error("Not logged in — call login() first");
    }

    const issuer = this.opts.issuer || "";
    const params = new URLSearchParams({
      access_token: session.access_token,
      path: options.path || "/",
      return_url: options.returnUrl,
    });

    const url = `${issuer}/api/v1/sso-entry?${params.toString()}`;
    if (this.win.location) {
      this.win.location.href = url;
    }
  }

  /**
   * Handle the redirect back from User Center.
   * Call this on your `returnUrl` page — it checks for `?code=uc_xxx` in the URL,
   * exchanges the code for tokens, and cleans up the URL.
   *
   * Returns null if no code is present in the URL (normal page load).
   */
  async handleUserCenterReturn(): Promise<ThirdPartyCodeExchangeResult | null> {
    if (!this.win.location) return null;

    const url = new URL(
      typeof this.win.location.href === "string"
        ? this.win.location.href
        : "",
    );
    const code = url.searchParams.get("code");
    if (!code || !code.startsWith("uc_")) return null;

    // Exchange code for tokens
    const result = await this.exchangeAuthCode(code);

    // Clean up URL
    url.searchParams.delete("code");
    url.searchParams.delete("state");
    const cleanUrl = url.pathname + (url.search ? url.search : "") + url.hash;
    if (typeof history !== "undefined" && history.replaceState) {
      history.replaceState(null, "", cleanUrl);
    }

    return result;
  }

  // ─── OIDC (existing) ───

  async beginLogin(loginOptions: BrowserLoginOptions = {}): Promise<BrowserLoginResult> {
    const query = new URLSearchParams({
      client_id: this.opts.clientId,
      redirect_uri: this.opts.redirectUri,
      response_type: "code",
      scope: this.opts.scope,
      state: loginOptions.state ?? "",
      nonce: loginOptions.nonce ?? "",
      code_challenge: "browser-sdk-pkce-placeholder",
      code_challenge_method: "S256",
    });
    const response = await this.getJSON<AuthorizeStartResult>(
      `${this.opts.issuer}/oauth2/authorize?${query.toString()}`,
    );
    return {
      authRequestId: response.auth_request_id,
      loginPage: response.login_page,
      consentPage: response.consent_page,
    };
  }

  async loginWithPopup(loginOptions: BrowserLoginOptions = {}) {
    const result = await this.beginLogin(loginOptions);
    const features = ["popup=yes", "width=540", "height=720", "resizable=yes", "scrollbars=yes"].join(",");
    this.win.open?.(result.loginPage, "auth-center-login", features);
    return result;
  }

  async loginWithRedirect(loginOptions: BrowserLoginOptions = {}) {
    const result = await this.beginLogin(loginOptions);
    if (this.win.location) {
      this.win.location.href = result.loginPage;
    }
    return result;
  }

  handlePopupMessage(event: MessageEvent): PopupAuthPayload | null {
    const payload = event?.data as
      | { type?: string; ok?: boolean; data?: PopupAuthPayload }
      | undefined;
    if (!payload || payload.type !== "user-center-oauth" || !payload.ok) {
      return null;
    }
    return payload.data ?? null;
  }

  // ─── Token Management ───

  /** Refresh the access token using refresh_token. */
  async refreshToken(): Promise<TokenSession | null> {
    const session = this.getSession();
    if (!session?.refresh_token) return null;

    try {
      const data = await this.postJSON<{ access_token: string; refresh_token?: string; expires_in?: number }>(
        "/oauth2/token",
        {
          grant_type: "refresh_token",
          refresh_token: session.refresh_token,
          client_id: this.opts.clientId,
        },
      );
      const updated: TokenSession = {
        ...session,
        access_token: data.access_token,
        refresh_token: data.refresh_token ?? session.refresh_token,
        expires_in: data.expires_in ?? 0,
        expires_at: data.expires_in ? Date.now() + data.expires_in * 1000 : session.expires_at,
      };
      this.setSession(updated);
      return updated;
    } catch {
      return null;
    }
  }

  /** Get a valid access token, refreshing if needed. */
  async getValidToken(): Promise<string | null> {
    const session = this.getSession();
    if (!session?.access_token) return null;

    if (session.expires_at && Date.now() >= session.expires_at - 60_000) {
      const refreshed = await this.refreshToken();
      return refreshed?.access_token ?? null;
    }
    return session.access_token;
  }

  // ─── Session Storage ───

  setSession(payload: PopupAuthPayload | TokenSession | Record<string, unknown>): void {
    const session = payload as TokenSession;
    if (session.expires_in && !session.expires_at) {
      session.expires_at = Date.now() + session.expires_in * 1000;
    }
    this.storage.setItem(SESSION_KEY, JSON.stringify(session));
  }

  getSession(): TokenSession | null {
    const raw = this.storage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as TokenSession;
  }

  clearSession(): void {
    this.storage.removeItem(SESSION_KEY);
  }

  // ─── Phone binding ───

  /**
   * Complete OAuth signup for a new user who needs to bind a phone/email.
   * Called after exchangeOAuthCode returns next_step: "complete_signup".
   * @param bindToken - the oauth_bind_token from the exchange response
   * @param phone - phone number
   * @param smsCode - SMS verification code
   * @param password - password for the new account
   * @param account - optional account name (defaults to phone or email from bind token)
   */
  async completeOAuthSignup(
    bindToken: string,
    phone: string,
    smsCode: string,
    password: string,
    account?: string,
  ): Promise<TokenSession> {
    const data = await this.postJSON<{
      access_token: string;
      refresh_token?: string;
      expires_in?: number;
      account_id?: number;
      display_id?: string;
      redirect_uri?: string;
    }>("/api/v1/user/oauth/complete-signup", {
      oauth_bind_token: bindToken,
      phone,
      code: smsCode,
      password,
      account: account || phone,
    });
    if (data.access_token) {
      this.saveSession(data);
    }
    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_in: data.expires_in || 0,
      expires_at: Date.now() + (data.expires_in || 1800) * 1000,
    };
  }

  /**
   * Send an SMS verification code to a phone number (for binding).
   * Uses the "login" type by default — no prior login required to send code.
   */
  async sendBindSMSCode(phone: string): Promise<void> {
    await this.sendSMSCode(phone, "login");
  }

  /**
   * Bind a phone number to the current logged-in user.
   * Requires prior login (access_token in session).
   * @param phone - phone number (e.g. "13800000000")
   * @param smsCode - the verification code received via SMS
   */
  async bindPhone(phone: string, smsCode: string): Promise<void> {
    const session = this.getSession();
    if (!session?.access_token) {
      throw new Error("Not logged in — call login() first");
    }
    const issuer = this.opts.issuer || "";
    const fetchFn = this.opts.fetchImpl || fetch;
    const resp = await fetchFn(`${issuer}/api/v1/user/phone/bind`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ phone, sms_code: smsCode }),
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error((data as Record<string, string>).message || "bind phone failed");
    }
  }

  /**
   * Unbind the phone number from the current logged-in user.
   */
  async unbindPhone(): Promise<void> {
    const session = this.getSession();
    if (!session?.access_token) {
      throw new Error("Not logged in — call login() first");
    }
    const issuer = this.opts.issuer || "";
    const fetchFn = this.opts.fetchImpl || fetch;
    const resp = await fetchFn(`${issuer}/api/v1/user/phone/unbind`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error((data as Record<string, string>).message || "unbind phone failed");
    }
  }

  /**
   * Check if a phone number is available for binding (not already bound to another user).
   */
  async checkPhoneAvailable(phone: string): Promise<boolean> {
    const session = this.getSession();
    if (!session?.access_token) throw new Error("Not logged in");
    const issuer = this.opts.issuer || "";
    const fetchFn = this.opts.fetchImpl || fetch;
    const resp = await fetchFn(`${issuer}/api/v1/user/phone/check`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ phone }),
    });
    if (!resp.ok) return false;
    const data = await resp.json().catch(() => ({}));
    return data.code === 0;
  }

  /**
   * Change the phone number for the current logged-in user.
   * @param newPhone - the new phone number
   * @param smsCode - SMS verification code for the new phone
   */
  async changePhone(newPhone: string, smsCode: string): Promise<void> {
    const session = this.getSession();
    if (!session?.access_token) throw new Error("Not logged in");
    const issuer = this.opts.issuer || "";
    const fetchFn = this.opts.fetchImpl || fetch;
    const resp = await fetchFn(`${issuer}/api/v1/user/phone/change`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ new_phone: newPhone, new_sms_code: smsCode }),
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error((data as Record<string, string>).message || "change phone failed");
    }
  }

  // ─── Logout ───

  /** Logout: notify server and clear local session. */
  async logout(): Promise<void> {
    const session = this.getSession();
    if (session?.access_token) {
      try {
        await this.postJSON("/api/v1/user/logout", {
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        });
      } catch {
        // ignore server errors during logout
      }
    }
    this.clearSession();
  }

  // ─── HTTP Helpers ───

  private async getJSON<T>(url: string): Promise<T> {
    const resp = await this.fetch(url);
    if (!resp.ok) throw new Error(`request failed: ${resp.status}`);
    const body = (await resp.json()) as { data: T };
    return body.data;
  }

  private async postJSON<T>(url: string, body: Record<string, unknown>): Promise<T> {
    const resp = await this.fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const result = (await resp.json()) as { code?: number; message?: string; data: T };
    if (result.code !== undefined && result.code !== 0) {
      throw new Error(result.message ?? `API error (code ${result.code})`);
    }
    return result.data;
  }

  private saveSession(data: { access_token: string; refresh_token?: string; expires_in?: number; account_id?: number; display_id?: string; user_email?: string; user_nickname?: string }) {
    const session: TokenSession = {
      access_token: data.access_token,
      refresh_token: data.refresh_token ?? "",
      expires_in: data.expires_in ?? 0,
      expires_at: data.expires_in ? Date.now() + data.expires_in * 1000 : undefined,
      account_id: data.account_id ?? 0,
      display_id: data.display_id,
      email: data.user_email,
      nickname: data.user_nickname,
    };
    this.setSession(session);
  }
}

// in-memory fallback for non-browser environments
function memStorage(): StorageLike {
  const store = new Map<string, string>();
  return {
    getItem: (k) => store.get(k) ?? null,
    setItem: (k, v) => store.set(k, v),
    removeItem: (k) => store.delete(k),
  };
}
